import localStorage from 'local-storage';
import { MENU_ORDER_TYPE_MAPPINGS, ORDER_PAYMENT_TYPE } from 'helpers/enums';

const TRACKED_PURCHASES_KEY = 'no_tracked_purchases';
const TRACKED_PURCHASES_KEPT = 20;

const trackedPurchases = new Set();

const PAYMENT_TYPE_LABELS = {
  [ORDER_PAYMENT_TYPE.unpaid]: 'unpaid',
  [ORDER_PAYMENT_TYPE.cash]: 'cash',
  [ORDER_PAYMENT_TYPE.card_online]: 'card_online',
  [ORDER_PAYMENT_TYPE.card_in_store]: 'card_in_store',
  [ORDER_PAYMENT_TYPE.mobile_terminal]: 'mobile_terminal',
  [ORDER_PAYMENT_TYPE.custom]: 'custom',
};

export const gtmEnabled = () =>
  typeof window !== 'undefined' && !!window.__gtmId;

export const ga4Enabled = () =>
  typeof window !== 'undefined' &&
  !!window.__ga4Id &&
  typeof window.gtag === 'function';

export const pixelEnabled = () =>
  typeof window !== 'undefined' &&
  !!window.__pixelId &&
  typeof window.fbq === 'function';

const anyChannelEnabled = () => gtmEnabled() || ga4Enabled() || pixelEnabled();

const isUkStore = () =>
  typeof window !== 'undefined' &&
  window.storeConfig?.countryIdentifier === 'GB';

function debug(channel, name, params) {
  if (typeof window !== 'undefined' && window.__analyticsDebug) {
    // eslint-disable-next-line no-console
    console.log(`[analytics] ${channel} → ${name}`, params ?? {});
  }
}

function observe(name, run) {
  try {
    run();
  } catch (error) {
    debug('error', name, error);
  }
}

let sendImmediately = false;

function dispatch(channel, name, send) {
  const run = () => {
    try {
      send();
    } catch (error) {
      debug('error', `${channel} ${name}`, error);
    }
  };
  if (sendImmediately) run();
  else setTimeout(run, 0);
}

export function gtmPush(event, params = {}) {
  if (!gtmEnabled()) return;
  dispatch('gtm', event, () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...params });
    debug('gtm', event, params);
  });
}

export function gtmEcommercePush(event, ecommerce, params = {}) {
  if (!gtmEnabled()) return;
  dispatch('gtm', event, () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event, ...params, ecommerce });
    debug('gtm', event, { ...params, ecommerce });
  });
}

export function ga4Event(name, params = {}) {
  if (!ga4Enabled()) return;
  dispatch('ga4', name, () => {
    window.gtag('event', name, params);
    debug('ga4', name, params);
  });
}

export function pixelTrack(name, params = {}) {
  if (!pixelEnabled()) return;
  dispatch('pixel', name, () => {
    window.fbq('track', name, params);
    debug('pixel', name, params);
  });
}

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const getCurrency = () =>
  typeof window !== 'undefined' ? window.storeConfig?.currencyCode : undefined;

let categoryNames = null;
function categoryName(categoryId) {
  if (!categoryId) return undefined;
  if (categoryNames === null || categoryNames.size === 0) {
    const list =
      typeof window !== 'undefined' ? window.categoriesList || [] : [];
    categoryNames = new Map(
      list.filter(Boolean).map((category) => [category._id, category.name])
    );
  }
  return categoryNames.get(categoryId) ?? categoryId;
}

function itemVariant(item) {
  const names = [
    ...(item.selectedSizes || []),
    ...(item.selectedSizesHalf || []),
  ]
    .filter((size) => size && Number(size.quantity) > 0 && size.name)
    .map((size) => size.name);

  return names.length ? [...new Set(names)].join(', ') : undefined;
}

export function toAnalyticsItems(menuItems = []) {
  return (menuItems || []).filter(Boolean).map((item, index) => {
    const quantity = Number(item.quantity) || 1;
    const lineTotal = Number(item.payablePrice ?? item.itemPrice) || 0;
    const category = categoryName(item.categoryId);
    const variant = itemVariant(item);

    return {
      item_id: item._id,
      item_name: item.name,
      ...(category && { item_category: category }),
      ...(variant && { item_variant: variant }),
      price: round2(lineTotal / quantity),
      quantity,
      index,
    };
  });
}

const sumItemValue = (items) =>
  round2(items.reduce((total, item) => total + item.price * item.quantity, 0));

export function diffAddedItems(before, after) {
  const keyOf = (item) => `${item._id}:${item.orderIndex ?? 0}`;
  const previous = new Map(
    (before || [])
      .filter(Boolean)
      .map((item) => [keyOf(item), Number(item.quantity) || 0])
  );

  return (after || []).filter(Boolean).reduce((added, item) => {
    const quantity = Number(item.quantity) || 0;
    const delta = quantity - (previous.get(keyOf(item)) || 0);
    if (delta <= 0) return added;

    const lineTotal = Number(item.payablePrice ?? item.itemPrice) || 0;
    added.push({
      ...item,
      quantity: delta,
      payablePrice: quantity > 0 ? (lineTotal / quantity) * delta : lineTotal,
    });
    return added;
  }, []);
}

export const diffRemovedItems = (before, after) =>
  diffAddedItems(after, before);

function ga4Payload(items, value, extra = {}) {
  const currency = getCurrency();
  return {
    ...(value !== undefined && { value: round2(value) }),
    ...(currency && { currency }),
    items,
    ...extra,
  };
}

function pixelPayload(items, value, extra = {}) {
  const currency = getCurrency();
  const names = items.map((item) => item.item_name).filter(Boolean);

  return {
    content_type: 'product',
    content_ids: items.map((item) => item.item_id),
    ...(names.length && { content_name: names.join(', ') }),
    contents: items.map((item) => ({
      id: item.item_id,
      quantity: item.quantity,
      item_price: item.price,
    })),
    num_items: items.reduce((total, item) => total + item.quantity, 0),
    ...(value !== undefined && { value: round2(value) }),
    ...(currency && { currency }),
    ...extra,
  };
}

export function orderContext(currentOrder) {
  const { orderType, paymentType } = currentOrder || {};
  const orderTypeLabel =
    orderType === undefined || orderType === null || orderType === ''
      ? undefined
      : MENU_ORDER_TYPE_MAPPINGS[orderType] ?? String(orderType);

  return {
    ...(orderTypeLabel && { order_type: orderTypeLabel }),
    ...(PAYMENT_TYPE_LABELS[paymentType] && {
      payment_type: PAYMENT_TYPE_LABELS[paymentType],
    }),
  };
}

function emitEcommerce(ga4Name, pixelName, ga4Params, pixelParams) {
  if (isUkStore()) {
    debug('skip', `${ga4Name} (UK store)`);
    return;
  }

  const { value, currency, items, transaction_id, shipping, ...context } =
    ga4Params;

  gtmEcommercePush(
    ga4Name,
    {
      ...(transaction_id && { transaction_id }),
      ...(value !== undefined && { value }),
      ...(currency && { currency }),
      ...(shipping !== undefined && { shipping }),
      items,
    },
    context
  );
  ga4Event(ga4Name, { ...ga4Params });
  if (pixelName) pixelTrack(pixelName, pixelParams);
}

function listContextFor(item) {
  const category = categoryName(item?.categoryId);
  return {
    ...(item?.categoryId && { item_list_id: item.categoryId }),
    ...(category && { item_list_name: category }),
  };
}

function buildMenuItem(item, unitPrice, listContext = {}) {
  const price = round2(unitPrice);
  const category = categoryName(item.categoryId);
  return {
    item_id: item._id,
    item_name: item.name,
    ...(category && { item_category: category }),
    ...(price > 0 && { price }),
    quantity: 1,
    index: 0,
    ...listContext,
  };
}

export function trackSelectItem(item, unitPrice) {
  observe('select_item', () => {
    if (!item) return;
    const listContext = listContextFor(item);
    const items = [buildMenuItem(item, unitPrice, listContext)];

    emitEcommerce('select_item', null, { items, ...listContext }, undefined);
  });
}

export function trackViewItem(item, unitPrice) {
  observe('view_item', () => {
    if (!item) return;
    const items = [buildMenuItem(item, unitPrice, listContextFor(item))];
    const value = items[0].price;

    emitEcommerce(
      'view_item',
      'ViewContent',
      ga4Payload(items, value),
      pixelPayload(items, value)
    );
  });
}

export function trackAddToCart(addedItems, currentOrder) {
  observe('add_to_cart', () => {
    const items = toAnalyticsItems(
      Array.isArray(addedItems) ? addedItems : [addedItems]
    );
    if (!items.length) return;

    const value = sumItemValue(items);
    const context = orderContext(currentOrder);
    emitEcommerce(
      'add_to_cart',
      'AddToCart',
      ga4Payload(items, value, context),
      pixelPayload(items, value, context)
    );
  });
}

export function trackRemoveFromCart(removedItems, currentOrder) {
  observe('remove_from_cart', () => {
    const items = toAnalyticsItems(
      Array.isArray(removedItems) ? removedItems : [removedItems]
    );
    if (!items.length) return;

    const value = sumItemValue(items);
    const context = orderContext(currentOrder);
    emitEcommerce(
      'remove_from_cart',
      null,
      ga4Payload(items, value, context),
      undefined
    );
  });
}

export function trackBeginCheckout(currentOrder) {
  observe('begin_checkout', () => {
    const items = toAnalyticsItems(currentOrder?.menuItems);
    if (!items.length) return;

    const value = currentOrder?.payableAmount ?? sumItemValue(items);
    const context = orderContext(currentOrder);
    emitEcommerce(
      'begin_checkout',
      'InitiateCheckout',
      ga4Payload(items, value, context),
      pixelPayload(items, value, context)
    );
  });
}

export function trackAddPaymentInfo(currentOrder) {
  observe('add_payment_info', () => {
    const items = toAnalyticsItems(currentOrder?.menuItems);
    if (!items.length) return;

    const value = currentOrder?.payableAmount ?? sumItemValue(items);
    const context = orderContext(currentOrder);
    emitEcommerce(
      'add_payment_info',
      'AddPaymentInfo',
      ga4Payload(items, value, context),
      pixelPayload(items, value, context)
    );
  });
}

function isDuplicatePurchase(transactionId) {
  if (!transactionId) return false;
  if (trackedPurchases.has(transactionId)) return true;

  let seen = [];
  try {
    const stored = localStorage.get(TRACKED_PURCHASES_KEY);
    if (Array.isArray(stored)) seen = stored;
  } catch (error) {
    debug('error', 'purchase ledger read', error);
  }

  trackedPurchases.add(transactionId);
  if (seen.includes(transactionId)) return true;

  try {
    localStorage.set(
      TRACKED_PURCHASES_KEY,
      [...seen, transactionId].slice(-TRACKED_PURCHASES_KEPT)
    );
  } catch (error) {
    debug('error', 'purchase ledger write', error);
  }
  return false;
}

export function trackPurchase(currentOrder, transactionId) {
  observe('purchase', () => {
    const items = toAnalyticsItems(currentOrder?.menuItems);
    if (!items.length) return;

    if (isUkStore() || !anyChannelEnabled()) {
      debug('skip', `purchase (${isUkStore() ? 'UK store' : 'no channels'})`);
      return;
    }

    if (isDuplicatePurchase(transactionId)) {
      debug('skip', 'purchase (already tracked)', { transactionId });
      return;
    }

    const value = currentOrder?.payableAmount ?? sumItemValue(items);
    const shipping = Number(currentOrder?.deliveryCost) || 0;
    const extra = {
      ...orderContext(currentOrder),
      ...(transactionId && { transaction_id: String(transactionId) }),
      ...(shipping > 0 && { shipping: round2(shipping) }),
    };

    emitEcommerce(
      'purchase',
      'Purchase',
      ga4Payload(items, value, extra),
      pixelPayload(items, value, extra)
    );
  });
}

function screenPageLocation(screenName) {
  if (typeof window === 'undefined') return undefined;
  return `${window.location.origin}/${screenName}`;
}

export function trackScreenView(screenName) {
  observe('page_view', () => {
    if (typeof window === 'undefined') return;

    const params = {
      page_location: screenPageLocation(screenName),
      page_title: screenName,
      screen_name: screenName,
      engagement_time_msec: 1,
    };

    gtmPush('page_view', params);
    ga4Event('page_view', params);
  });
}

export function trackScreenEngagement(
  screenName,
  engagementTimeMsec,
  { immediate = false } = {}
) {
  observe('screen_engagement', () => {
    const params = {
      screen_name: screenName,
      engagement_time_msec: engagementTimeMsec,
    };

    sendImmediately = immediate;
    try {
      gtmPush('screen_engagement', params);
      ga4Event('screen_engagement', params);
    } finally {
      sendImmediately = false;
    }
  });
}
